import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { SurveyCreator, SurveyCreatorComponent } from 'survey-creator-react';
import 'survey-core/survey.i18n';
import 'survey-creator-core/survey-creator-core.i18n';
import { settings } from 'survey-creator-core';
import { applyV1Defaults } from './survey_v1_defaults';
import Alert from '@mui/material/Alert';

settings.toolbox.defaultJSON.matrixdropdown = {
  columns: [
    {
      name: 'column1',
      cellType: 'radiogroup',
      isRequired: true,
      showInMultipleColumns: true,
      choices: ['item1', 'item2', 'item3', 'item4', 'item5'],
    },
  ],
  rows: ['item1', 'item2', 'item3'],
};

const questionTypes = [
  'question',
  'text',
  'checkbox',
  'radiogroup',
  'dropdown',
  'boolean',
  'matrix',
  'matrixdropdown',
  'rating',
  'ranking',
  'comment',
  'panel',
  'html',
];

const options = {
  autoSaveEnabled: true,
  showState: true,
  showThemeTab: false,
  showJSONEditorTab: false,
  showPreviewTab: false,
  showLogicTab: false,
  showToolbox: false,
  questionTypes: questionTypes,
  showTranslationTab: true,
};

const SurveyDesigner = ({ survey, onSave, hasResponses }) => {
  const [surveyContent] = useState(() => {
    let initialContent = survey.content;
    return applyV1Defaults({
      ...initialContent,
      title: initialContent.title || survey.name,
      description: initialContent.description || survey.description,
      questionTitlePattern: 'numRequireTitle',
      widthMode: 'responsive',
    });
  });

  const creator = useMemo(() => new SurveyCreator(options), []);

  creator.saveSurveyFunc = async (no, callback) => {
    try {
      await onSave(creator.JSON);
      callback(no, true);
      updateTitle(creator.JSON.title);
    } catch (error) {
      console.error(error);
      callback(no, false);
    }
  };

  useEffect(() => {
    creator.showSidebar = false;
  }, [creator]);

  useEffect(() => {
    creator.JSON = surveyContent;
  }, [surveyContent]);

  const updateTitle = (title) => {
    const headerElement = document.querySelector('.project-nav h1');
    if (headerElement) {
      const titleText = typeof title === 'object' ? title.default || Object.values(title)[0] : title;
      headerElement.innerText = titleText;
    }
  };

  // Properties to hide from the property grid. When all properties inside a category are hidden,
  // the category is hidden as well.
  const propertyStopList = [
    'widthMode',
    'mode',
    'cookieName',
    'firstPageIsStartPage',
    'showPrevButton',
    'autoAdvanceEnabled',
    'showCompletePage',
    'navigateToUrl',
    'completedHtml',
    'navigateToUrlOnCondition',
    'completedHtmlOnCondition',
    'loadingHtml',
    'completedBeforeHtml',
    'rowTitleWidth',
    'autoFocusFirstQuestion',
  ];

  creator.onPropertyShowing.add((sender, options) => {
    options.show = !propertyStopList.includes(options.property.name);

    // v2 also exposes showCommentArea on individual choices. Enabling it on any single
    // choice flips the stored answer from ["Option1"] to [{ value: "Option1" }], a shape
    // the reporting ETL silently drops. Question-level comment areas stay editable.
    if (options.show && options.property.name === 'showCommentArea' && options.obj.isDescendantOf('choiceitem')) {
      options.show = false;
    }
  });

  return (
    <>
      {hasResponses && (
        <Alert severity="warning" className="my-4 py-3">
          <h6 className="mb-0">
            <strong>Caution:</strong> This survey already has responses; editing questions or options could compromise the integrity of existing data
            and reports, except for minor text adjustments like fixing typos.
          </h6>
        </Alert>
      )}
      <div className="survey survey--designer" style={{ height: '100vh' }}>
        <SurveyCreatorComponent creator={creator} />
      </div>
    </>
  );
};

SurveyDesigner.propTypes = {
  survey: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  hasResponses: PropTypes.bool,
};

export default SurveyDesigner;
